<template>
  <v-form>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-finance-mode')">
      <m-select
        v-model="selectedMode"
        :itemText="item => $t(`offer-manager.create.step-financial-services.finance-mode.${item.name}`)"
        :items="modes"
        :readonly="modes.length === 1"
        return-object
        data-cy="finance-mode"
        :disabled="disabled"
        :loading="loading" />
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-price-of-goods')">
      <price-field
        required
        v-model="input.priceOfGoodsAmount"
        :min="minPriceOfGoods"
        :rules="[isRequired, r => isMinAmount(r, minPriceOfGoods)]"
        :key="minPriceOfGoods"
        data-cy="finance-price-of-goods"
        :disabled="disabled"
        :loading="loading" />
    </m-form-row>
    <m-form-row>
      <template v-slot:custom-label>
        <div class="align-self-start">{{ $t('offer-manager.create.step-financial-services.lbl-deposit') }}</div>
      </template>
      <template v-slot:default>
        <price-field
          v-model="input.depositAmount"
          data-cy="finance-deposit"
          :min="minDepositAmount"
          :max="maxDepositAmount"
          :key="`${minDepositAmount}-${maxDepositAmount}`"
          :disabled="disabled || isDepositInfoEqual"
          :loading="loading" />
        <div class="d-flex align-center justify-start pt-4">
          <v-icon dense class="fa-light fa-circle-info pr-2" color="tertiary" />
          <span>{{ depositInfo }}</span>
        </div>
      </template>
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-ppp')">
      <m-radio-group
        v-model="input.paymentProtectionInsurance"
        :row="true"
        :items="[
          {label: $t('buttons.yes'), value: true},
          {label: $t('buttons.no'), value: false},
        ]"
        data-cy="finance-ppp"
        :disabled="disabled"
        :loading="loading" />
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-fee-amount')">
      <price-field v-model="result.feeAmount" readonly data-cy="finance-fee-amount" :loading="loading" />
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-ppp-amount')">
      <price-field v-model="result.pppAmount" readonly data-cy="finance-ppp-amount" :loading="loading" />
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-terms')">
      <m-text-field v-model="result.terms" readonly data-cy="finance-terms" :loading="loading" />
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-regular-payment')">
      <price-field
        v-model="result.monthlyInstallmentAmount"
        readonly
        data-cy="finance-regular-payment"
        :loading="loading" />
    </m-form-row>
    <m-form-row
      :label="$t('offer-manager.create.step-financial-services.lbl-final-payment')"
      v-if="selectedMode.name === 'OPTIONS'">
      <price-field
        v-model="result.finalInstallmentAmount"
        readonly
        data-cy="finance-final-payment"
        :loading="loading" />
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-tin')">
      <m-percent-field v-model="result.tin" readonly data-cy="finance-tin" :loading="loading" />
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-tae')">
      <m-percent-field v-model="result.tae" readonly data-cy="finance-tae" :loading="loading" />
    </m-form-row>
    <m-form-row>
      <v-alert
        v-if="result.monthlyInstallmentAmount <= 0"
        class="justify-center font-weight-bold text-body-2"
        type="warning"
        icon="fa-solid fa-triangle-exclamation"
        text>
        {{ $t('offer-manager.create.step-financial-services.alerts.negative-monthly-installment-amount') }}
      </v-alert>
    </m-form-row>
    <m-form-row :label="$t('offer-manager.create.step-financial-services.lbl-total-price')" highlighted>
      <price-field v-model="result.totalAmount" readonly data-cy="finance-total-price" :loading="loading" />
    </m-form-row>
  </v-form>
</template>

<script lang="ts">
//@ts-nocheck
import {formatNumberToCurrency} from '@mapit/common-utils'
import {Country} from '@mapit/common-utils/dist/types'
import {
  FinanceCalculator,
  FinanceConfig,
  FinanceOpts,
  FinanceResult,
} from '@muvio/honda-offers-builder/finance-calculator'
import Vue, {PropType} from 'vue'

import PriceField from '@/components/general/PriceField.vue'
import {isMinAmount, isRequired} from '@/helpers/validations'

interface FinancialCalculatorModel {
  mode?: FinanceConfig
  input?: FinanceOpts
  result?: FinanceResult
}
export default Vue.extend({
  name: 'FinancialCalculatorForm',
  components: {PriceField},
  props: {
    value: {
      type: Object as () => FinancialCalculatorModel,
      required: false,
    },
    modes: {
      type: Array as () => FinanceConfig[],
    },
    mode: Object as () => FinanceConfig,
    priceOfGoodsAmount: Number,
    vehiclePriceAmount: Number,
    additionalResidualValueAmount: Number,
    depositAmount: Number,
    paymentProtectionInsurance: Boolean,
    country: {
      type: String as PropType<Country>,
      default: Country.Spain,
    },
    totalPrice: Number,
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formatNumberToCurrency,
      isMinAmount,
      isRequired,
      selectedMode: this.mode || this.modes[0],
      input: {
        priceOfGoodsAmount: this.priceOfGoodsAmount,
        vehiclePriceAmount: this.vehiclePriceAmount,
        additionalResidualValueAmount: this.additionalResidualValueAmount,
        depositAmount: this.depositAmount || this.priceOfGoodsAmount * (this.mode || this.modes[0])?.depositRate,
        paymentProtectionInsurance: this.paymentProtectionInsurance || false,
      },
      minDepositAmount: this.priceOfGoodsAmount * (this.mode || this.modes[0])?.depositRate,
    }
  },
  computed: {
    result: {
      get(): FinanceResult {
        return new FinanceCalculator().calculate(this.input, this.selectedMode)
      },
      set(): void {
        // ignored
      },
    },
    maxDepositAmount() {
      const removedPrice =
        this.selectedMode && this.selectedMode.name === 'OPTIONS' ? this.result?.finalInstallmentAmount || 0 : 1000

      return this.priceOfGoodsAmount - removedPrice
    },
    minPriceOfGoods() {
      const removedPrice =
        this.selectedMode && this.selectedMode.name === 'OPTIONS' ? this.result?.finalInstallmentAmount || 0 : 1000

      return removedPrice / 0.85
    },
    isDepositInfoEqual() {
      const epsilon = 0.001
      const isEq = (a: number, b: number) => Math.abs(a - b) < epsilon

      return isEq(this.minDepositAmount, this.maxDepositAmount)
    },
    depositInfo() {
      return this.isDepositInfoEqual
        ? this.$t('offer-manager.create.step-financial-services.lbl-same-deposit-error', {
            deposit: formatNumberToCurrency(this.minDepositAmount, this.country),
          })
        : this.$t('offer-manager.create.step-financial-services.lbl-deposit-range-error', {
            minDeposit: formatNumberToCurrency(this.minDepositAmount, this.country),
            maxDeposit: formatNumberToCurrency(this.maxDepositAmount, this.country),
          })
    },
  },
  watch: {
    totalPrice(newVal) {
      this.input.priceOfGoodsAmount = newVal
      this.input.depositAmount = newVal * this.selectedMode?.depositRate
      this.minDepositAmount = newVal * this.selectedMode?.depositRate
      this.$emit('input', {
        mode: this.selectedMode,
        input: this.input,
        result: this.result,
      })
    },
    'input.priceOfGoodsAmount'(newVal) {
      this.input.depositAmount = newVal * this.selectedMode?.depositRate
      this.minDepositAmount = newVal * this.selectedMode?.depositRate
    },
    priceOfGoodsAmount(newVal) {
      this.input.priceOfGoodsAmount = newVal
      this.minDepositAmount = newVal * this.selectedMode?.depositRate
    },
    selectedMode(newVal) {
      this.minDepositAmount = this.input.priceOfGoodsAmount * newVal.depositRate
    },
    depositAmount(newVal) {
      if (newVal) {
        this.input.depositAmount = newVal
      }
    },
    vehiclePriceAmount(newVal) {
      this.input.vehiclePriceAmount = newVal
      this.minDepositAmount = this.input.priceOfGoodsAmount * this.selectedMode?.depositRate
    },
    additionalResidualValueAmount(newVal) {
      this.input.additionalResidualValueAmount = newVal
    },
    result: {
      immediate: true,
      handler(newVal) {
        this.$emit('input', {
          mode: this.selectedMode,
          input: this.input,
          result: newVal,
        })
      },
    },
  },
  created() {
    this.result = new FinanceCalculator().calculate(this.input, this.selectedMode)
  },
})
</script>
